import { ClerkProvider } from "@clerk/clerk-react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";

import "@/context/font-provider";
import "@/context/theme-provider";
import { describeError } from "@/lib/errors";
import { parseSearch, stringifySearch } from "@/lib/router-search";

import { routeTree } from "./routeTree.gen";
import "./styles/index.css";

const CLERK_PUBLISHABLE_KEY = (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? "").trim();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (import.meta.env.DEV) return failureCount < 1;
        if (failureCount > 3) return false;
        if (error instanceof Error) {
          const e = error as Error & { response?: { status?: number } };
          const status = e.response?.status;
          return status !== 401 && status !== 403;
        }
        return true;
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000,
    },
    mutations: {
      onError: (error) => {
        if (error instanceof Error) {
          const e = error as Error & { response?: { status?: number } };
          if (e.response?.status === 304) {
            toast.error("Content not modified!");
            return;
          }
        }
        toast.error(describeError(error).title);
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      toast.error(describeError(error).title);
    },
  }),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  parseSearch,
  stringifySearch,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// ponytail: cross-route back-target state. Carried in window.history.state
// (and therefore in router.navigate's `state` option + useLocation().state)
// so the document detail route can return the user to whichever list page
// they came from. Discriminated by `from` so the document route can branch
// on dashboard vs. company without parsing URL strings.
declare module "@tanstack/history" {
  interface HistoryState {
    documentDetailBack?:
      | { from: "dashboard"; year: number }
      | { from: "company"; cnpj: string; year?: number };
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
        <RouterProvider router={router} />
      </ClerkProvider>
    </QueryClientProvider>
  </StrictMode>,
);
